import { useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { format } from "date-fns"

import BasicButton from "@/components/button/BasicButton"
import IconButton from "@/components/button/IconButton"
import Checkbox from "@/components/form/checkbox/Checkbox"
import DatePicker from "@/components/form/datePicker/DatePicker"
import Selector from "@/components/form/selector/Selector"
import TextField from "@/components/form/textField/TextField"
import { DEFAULT_COVERAGE_OPTIONS } from "@/app/settings/analysis-groups/season/constants"
import { ActivityTypeKey, SeasonWeek } from "@/types/setting"
import { DATE_FORMAT } from "@/constants/date"
import { ListItemText, MenuItem } from "@mui/material"

export default function SeasonWeekRow({
  item,
  seasonWeeks,
  onChangeSeasonWeeks,
  openDialog,
  onMatchActivities,
}: {
  item: SeasonWeek
  seasonWeeks: SeasonWeek[]
  onChangeSeasonWeeks: (seasonWeeks: SeasonWeek[]) => void
  openDialog: () => void
  onMatchActivities: (idfSeasonWeek: number) => void
}) {
  const { active, startDate, endDate, coverage, newId, name } = item

  const { t } = useTranslation()
  const [newName, setNewName] = useState(name || "")
  const [newStartDate, setNewStartDate] = useState<string | null>(startDate)
  const [newEndDate, setNewEndDate] = useState<string | null>(endDate)
  const [selectedCoverage, setSelectedCoverage] = useState<string[]>(coverage)

  useEffect(() => {
    const index = seasonWeeks.findIndex((item) => {
      return item.newId === newId
    })

    onChangeSeasonWeeks([
      ...seasonWeeks.slice(0, index),
      {
        ...item,
        name: newName,
        startDate: newStartDate,
        endDate: newEndDate,
        coverage: selectedCoverage as ActivityTypeKey[],
      },
      ...seasonWeeks.slice(index + 1),
    ])
  }, [newName, newStartDate, newEndDate, selectedCoverage])

  const onChangeOption = (event: { target: { value: any } }) => {
    const value = event.target.value
    const newValue = typeof value === "string" ? value.split(",") : value
    setSelectedCoverage(newValue)
  }

  const convertDateForCompare = (date: string | Date) => {
    return new Date(new Date(date).setHours(0, 0, 0, 0))
  }

  // 종료일은 시작일로부터 촤대 365일까지 설정 가능
  const checkIsOverYear = (date: string | Date, type: "startDate" | "endDate") => {
    return (
      (type === "endDate" &&
        item.startDate &&
        convertDateForCompare(date) >=
          convertDateForCompare(
            new Date(
              new Date(item.startDate).setFullYear(new Date(item.startDate).getFullYear() + 1),
            ),
          )) ||
      (type === "startDate" &&
        item.endDate &&
        convertDateForCompare(date) <=
          convertDateForCompare(
            new Date(new Date(item.endDate).setFullYear(new Date(item.endDate).getFullYear() - 1)),
          ))
    )
  }

  // 같은 row 에서 시작일이 종료일보다 큰지 확인
  const checkIsDisabledDateInRow = (value: string | Date, type: "startDate" | "endDate") => {
    return (
      (type === "startDate" &&
        item.endDate &&
        convertDateForCompare(value) >= convertDateForCompare(item.endDate)) ||
      (type === "endDate" &&
        item.startDate &&
        convertDateForCompare(value) <= convertDateForCompare(item.startDate))
    )
  }

  const checkDisabledDate = (day: Date, type: "startDate" | "endDate") => {
    const isDisableDateInRow = checkIsDisabledDateInRow(day, type)
    const isOverYear = checkIsOverYear(day, type)

    return isDisableDateInRow || !!isOverYear
  }

  const getErrMessage = useCallback(
    (value: string | null, type: "startDate" | "endDate") => {
      if (!value) {
        return t("rules.required")
      }
      const isDisabledDate = checkDisabledDate(new Date(value), type)

      if (isDisabledDate) {
        return t("rules.error")
      }

      return ""
    },
    [seasonWeeks],
  )

  const coverageSelectorValue = useMemo(() => {
    if (DEFAULT_COVERAGE_OPTIONS.length === coverage.length) {
      return `${t("allSelected")}`
    }
    if (coverage.length === 0) {
      return null
    }

    return t("countSelected", { count: coverage.length })
  }, [coverage])

  return (
    <div className="SeasonWeekRowCont">
      <div className="FormCont">
        <div className="ItemCont">
          <p>{t("name")}</p>
          <TextField
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            error={!newName.trim()}
            showMessage={!newName.trim()}
            message={t("rules.required")}
            placeholder={t("season")}
          />
        </div>
        <div className="ItemCont">
          <p>{t("startDate")}</p>
          <DatePicker
            value={newStartDate ? new Date(newStartDate) : null}
            onChange={(value) => {
              if (value) {
                setNewStartDate(format(value, DATE_FORMAT))
              }
            }}
            error={!!getErrMessage(newStartDate, "startDate")}
            showMessage={!!getErrMessage(newStartDate, "startDate")}
            message={getErrMessage(newStartDate, "startDate")}
            defaultValueNull
            onYearChange={(value) => setNewStartDate(format(value, DATE_FORMAT))}
            onMonthChange={(value) => setNewStartDate(format(value, DATE_FORMAT))}
            shouldDisableDate={(day) => checkDisabledDate(day, "startDate")}
          />
        </div>
        <div className="ItemCont">
          <p>{t("endDate")}</p>
          <DatePicker
            disabled={!newStartDate}
            value={newEndDate ? new Date(newEndDate) : null}
            onChange={(value) => {
              if (value) {
                setNewEndDate(format(value, DATE_FORMAT))
              }
            }}
            error={!!getErrMessage(newEndDate, "endDate")}
            showMessage={!!getErrMessage(newEndDate, "endDate")}
            message={getErrMessage(newEndDate, "endDate")}
            defaultValueNull
            onYearChange={(value) => setNewEndDate(format(value, DATE_FORMAT))}
            onMonthChange={(value) => setNewEndDate(format(value, DATE_FORMAT))}
            shouldDisableDate={(day) => checkDisabledDate(day, "endDate")}
          />
        </div>
        <div className="ItemCont">
          <p>{t("coverage")}</p>
          <Selector
            type="select"
            multiple
            value={selectedCoverage}
            onChange={onChangeOption}
            displayEmpty
            renderValue={() =>
              coverageSelectorValue || <span className="placeholder-text">{t("select")}</span>
            }
            showMessage={selectedCoverage.length === 0}
            message={t("rules.required")}>
            {DEFAULT_COVERAGE_OPTIONS.map((option) => {
              const isSelected = selectedCoverage.includes(option.value)

              return (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={isSelected} size="medium" />
                  <ListItemText
                    primary={t(option.label)}
                    className={isSelected ? "selected-option" : ""}
                  />
                </MenuItem>
              )
            })}
          </Selector>
        </div>
        <div className="ItemCont RowButtonCont">
          <BasicButton
            type="default-outlined"
            size="md"
            disabled={!item.idfSeasonWeek}
            onClick={() => item.idfSeasonWeek && onMatchActivities(item.idfSeasonWeek)}>
            {t("button.apply")}
          </BasicButton>
          <IconButton type="ghost" icon="close" size="md" onClick={openDialog} />
        </div>
      </div>
    </div>
  )
}
